import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { resetDemoDataset } from '../api/journal.js';
import useAuthenticatedRequest from './useAuthenticatedRequest.js';
import { dispatchDataInvalidation, JOURNAL_DEPENDENT_RESOURCES } from '../utils/dataInvalidation.js';

// Single source of truth for "demo mode" — persisted in Clerk unsafeMetadata.
// Clerk's client SDK only allows the browser to write unsafeMetadata
// (publicMetadata is read-only from the client; it can only be written
// server-side via the Clerk Backend API). For a cosmetic-prefs toggle like
// this, unsafeMetadata is the right call — worst case a user flips the bit
// via dev tools and sees demo data instead of theirs.
//
// Also reads publicMetadata as a fallback so a server-side override (e.g. an
// admin force-enabling demo mode for a tester) still works.
export default function useDemoMode() {
  const { user, isLoaded } = useUser();
  const { isLoaded: authLoaded, isSignedIn } = useAuthenticatedRequest();
  const [localDemoMode, setLocalDemoMode] = useState(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem('tradapp.demoMode');
    return stored == null ? null : stored === 'true';
  });
  const [localShowDemoAccount, setLocalShowDemoAccount] = useState(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem('tradapp.showDemoAccount');
    return stored == null ? null : stored === 'true';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = () => {
      const stored = window.localStorage.getItem('tradapp.demoMode');
      setLocalDemoMode(stored == null ? null : stored === 'true');
      const showDemoStored = window.localStorage.getItem('tradapp.showDemoAccount');
      setLocalShowDemoAccount(showDemoStored == null ? null : showDemoStored === 'true');
    };
    window.addEventListener('storage', sync);
    window.addEventListener('tradapp-demo-mode-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('tradapp-demo-mode-change', sync);
    };
  }, []);

  const demoMode = useMemo(() => {
    if (localDemoMode !== null) return localDemoMode;
    if (!user) return false;
    const fromUnsafe = user.unsafeMetadata?.tradapp?.demoMode;
    if (fromUnsafe !== undefined) return Boolean(fromUnsafe);
    return Boolean(user.publicMetadata?.tradapp?.demoMode);
  }, [localDemoMode, user]);

  const showDemoAccount = useMemo(() => {
    if (localShowDemoAccount !== null) return localShowDemoAccount;
    if (!user) return true;
    const fromUnsafe = user.unsafeMetadata?.tradapp?.showDemoAccount;
    if (fromUnsafe !== undefined) return Boolean(fromUnsafe);
    const fromPublic = user.publicMetadata?.tradapp?.showDemoAccount;
    return fromPublic === undefined ? true : Boolean(fromPublic);
  }, [localShowDemoAccount, user]);

  const setDemoMode = useCallback(
    async (next) => {
      const enabled = Boolean(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('tradapp.demoMode', String(enabled));
        setLocalDemoMode(enabled);
        window.dispatchEvent(new Event('tradapp-demo-mode-change'));
      }
      if (user) {
        const unsafeMetadata = user.unsafeMetadata || {};
        const tradapp = unsafeMetadata.tradapp || {};
        await user.update({
          unsafeMetadata: {
            ...unsafeMetadata,
            tradapp: {
              ...tradapp,
              demoMode: enabled,
              demoUpdatedAt: new Date().toISOString(),
            },
          },
        });
        await user.reload();
      }
    },
    [user]
  );

  const setShowDemoAccount = useCallback(
    async (next) => {
      const enabled = Boolean(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('tradapp.showDemoAccount', String(enabled));
        setLocalShowDemoAccount(enabled);
        window.dispatchEvent(new Event('tradapp-demo-mode-change'));
      }
      if (user) {
        const unsafeMetadata = user.unsafeMetadata || {};
        await user.update({
          unsafeMetadata: {
            ...unsafeMetadata,
            tradapp: { ...(unsafeMetadata.tradapp || {}), showDemoAccount: enabled },
          },
        });
        await user.reload();
      }
    },
    [user]
  );

  const resetDemoAccount = useCallback(async () => {
    if (!authLoaded || !isSignedIn) throw new Error('Sign in before resetting Demo.');
    const result = await resetDemoDataset();
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('tradapp.demoMode', 'true');
      window.localStorage.setItem('tradapp.showDemoAccount', 'true');
      window.localStorage.setItem('tradapp.accountScope', JSON.stringify({ type: 'demo', accountId: null, label: 'Demo' }));
      setLocalDemoMode(true);
      setLocalShowDemoAccount(true);
      window.dispatchEvent(new Event('tradapp-demo-mode-change'));
      window.dispatchEvent(new Event('tradapp-account-scope-change'));
      window.dispatchEvent(new CustomEvent('tradapp-demo-reset', { detail: { datasetVersion: result.datasetVersion } }));
    }
    dispatchDataInvalidation({
      accountId: 'demo',
      resources: [...JOURNAL_DEPENDENT_RESOURCES, ...result.affectedResources],
      datasetVersion: result.datasetVersion,
      reason: 'demo_reset',
    });
    if (user) {
      const unsafeMetadata = user.unsafeMetadata || {};
      try {
        await user.update({
          unsafeMetadata: {
            ...unsafeMetadata,
            tradapp: {
              ...(unsafeMetadata.tradapp || {}),
              demoMode: true,
              showDemoAccount: true,
            },
          },
        });
        await user.reload();
      } catch (error) {
        if (import.meta.env?.DEV) console.warn('Demo reset succeeded, but the display preference could not be synced.', error);
      }
    }
    return result;
  }, [authLoaded, isSignedIn, user]);

  return { demoMode, setDemoMode, showDemoAccount, setShowDemoAccount, resetDemoAccount, isLoaded: isLoaded && authLoaded };
}
